/**
 * Pool settings page
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Loader2, Save, Settings as SettingsIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { usePoolSettings, useUpdatePoolSettings } from "@/hooks/usePoolSettings";

const settingsSchema = z.object({
  // Password policy
  passwordMinLength: z.number().min(1).max(128),
  passwordRequireUppercase: z.boolean(),
  passwordRequireLowercase: z.boolean(),
  passwordRequireNumbers: z.boolean(),
  passwordRequireSymbols: z.boolean(),
  // Session settings
  sessionDurationMinutes: z.number().min(1).max(43200), // 30 days max
  sessionRefreshEnabled: z.boolean(),
  maxConcurrentSessions: z.number().min(1).max(100),
  // Security settings
  maxFailedLoginAttempts: z.number().min(1).max(20),
  lockoutDurationMinutes: z.number().min(1).max(1440), // 24 hours max
  // Registration settings
  allowSelfRegistration: z.boolean(),
  requireEmailVerification: z.boolean(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;

export function Settings() {
  const { data: settings, isLoading, error } = usePoolSettings();
  const updateSettings = useUpdatePoolSettings();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
  });

  useEffect(() => {
    if (settings) {
      reset(settings);
    }
  }, [settings, reset]);

  const onSubmit = async (data: SettingsFormData) => {
    await updateSettings.mutateAsync(data);
  };

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/30">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <p className="ml-3 text-sm text-red-700 dark:text-red-300">
            {error instanceof Error ? error.message : "Failed to load settings"}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center space-x-3">
        <SettingsIcon className="h-8 w-8 text-gray-400" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pool Settings</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Password Policy */}
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Password Policy</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="passwordMinLength"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Minimum Length
                </label>
                <input
                  type="number"
                  id="passwordMinLength"
                  {...register("passwordMinLength", { valueAsNumber: true })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {errors.passwordMinLength && (
                  <p className="mt-1 text-sm text-red-600">{errors.passwordMinLength.message}</p>
                )}
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    {...register("passwordRequireUppercase")}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Require uppercase</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    {...register("passwordRequireLowercase")}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Require lowercase</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    {...register("passwordRequireNumbers")}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Require numbers</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    {...register("passwordRequireSymbols")}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Require symbols</span>
                </label>
              </div>
            </div>
          </div>

          {/* Session Settings */}
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Session Settings</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="sessionDurationMinutes"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Session Duration (minutes)
                </label>
                <input
                  type="number"
                  id="sessionDurationMinutes"
                  {...register("sessionDurationMinutes", { valueAsNumber: true })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {errors.sessionDurationMinutes && (
                  <p className="mt-1 text-sm text-red-600">{errors.sessionDurationMinutes.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="maxConcurrentSessions"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Max Concurrent Sessions
                </label>
                <input
                  type="number"
                  id="maxConcurrentSessions"
                  {...register("maxConcurrentSessions", { valueAsNumber: true })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {errors.maxConcurrentSessions && (
                  <p className="mt-1 text-sm text-red-600">{errors.maxConcurrentSessions.message}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    {...register("sessionRefreshEnabled")}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Enable session refresh</span>
                </label>
              </div>
            </div>
          </div>

          {/* Security Settings */}
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Security Settings</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="maxFailedLoginAttempts"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Max Failed Login Attempts
                </label>
                <input
                  type="number"
                  id="maxFailedLoginAttempts"
                  {...register("maxFailedLoginAttempts", { valueAsNumber: true })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {errors.maxFailedLoginAttempts && (
                  <p className="mt-1 text-sm text-red-600">{errors.maxFailedLoginAttempts.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="lockoutDurationMinutes"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Lockout Duration (minutes)
                </label>
                <input
                  type="number"
                  id="lockoutDurationMinutes"
                  {...register("lockoutDurationMinutes", { valueAsNumber: true })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {errors.lockoutDurationMinutes && (
                  <p className="mt-1 text-sm text-red-600">{errors.lockoutDurationMinutes.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Registration Settings */}
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">Registration Settings</h2>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register("allowSelfRegistration")}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Allow self-registration</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register("requireEmailVerification")}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Require email verification</span>
              </label>
            </div>
          </div>

          {/* Error message */}
          {updateSettings.error && (
            <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/30">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <p className="ml-3 text-sm text-red-700 dark:text-red-300">
                  {updateSettings.error instanceof Error ? updateSettings.error.message : "Failed to update settings"}
                </p>
              </div>
            </div>
          )}

          {/* Submit button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!isDirty || updateSettings.isPending}
              className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {updateSettings.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Settings
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
